import React from 'react';

import { MOUSEDOWN, KEYDOWN, } from '../../Shared/utils/eventNames'
import { ESCAPE, } from '../../Shared/utils/keyNames'

const CORE_REPORTS_LINKS = [
  {
    label: 'Activity Summary Report',
    url: '/teachers/classrooms/scorebook'
  },
  {
    label: 'Activity Analysis Report',
    url: '/teachers/progress_reports/diagnostic_reports/#/activity_packs'
  },
]

const DIAGNOSTIC_REPORTS_LINKS = [
  {
    label: 'My Diagnostics',
    url: '/teachers/progress_reports/diagnostic_reports/#/diagnostics'
  },
  {
    label: 'Diagnostic Insights',
    url: '/teachers/progress_reports/diagnostic_insights',
    new: true
  },
]

const PREMIUM_REPORTS_LINKS = [
  {
    label: 'Activity Scores Report',
    url: '/teachers/progress_reports/activities_scores_by_classroom'
  },
  {
    label: 'Concepts Report',
    url: '/teachers/progress_reports/concepts/students'
  },
  {
    label: 'Standards Report',
    url: '/teachers/progress_reports/standards/classrooms'
  },
  {
    label: 'Data Export',
    url: '/teachers/progress_reports/activity_sessions'
  },
]

const CURRICULUM_SPECIFIC_REPORTS_LINKS = (showSocialStudiesTab, showScienceTab) => {
  const socialStudiesTab = showSocialStudiesTab && {
    label: 'Social Studies Dashboard',
    url: '/teachers/progress_reports/social-studies/world-history-1200-to-present'
  }

  const scienceTab = showScienceTab && {
    label: 'Interdisciplinary Science Dashboard',
    url: '/teachers/progress_reports/interdisciplinary-science/building-ai-knowledge'
  }
  return [
    socialStudiesTab,
    scienceTab
  ].filter(Boolean)
}

const DropdownTab = ({ activeTab, tabName, dropdownItems, dropdownId }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef(null)
  const dropdownRef = React.useRef(null);
  const buttonRef = React.useRef(null);

  React.useEffect(() => {
    document.addEventListener(KEYDOWN, handleKeyDown);
    return () => {
      document.removeEventListener(KEYDOWN, handleKeyDown);
    };
  }, [dropdownRef, isOpen]);

  React.useEffect(() => {
    const container = containerRef.current;

    function handleFocusOut(event) {
      requestAnimationFrame(() => {
        if (
          container &&
          !container.contains(document.activeElement)
        ) {
          closeDropdown();
        }
      });
    }

    if (container) {
      container.addEventListener('focusout', handleFocusOut);
    }

    return () => {
      if (container) {
        container.removeEventListener('focusout', handleFocusOut);
      }
    };
  }, [isOpen]);


  const activeClass = activeTab === tabName ? 'active' : '';
  const openClass = isOpen ? 'open' : '';

  function toggleDropdown() {
    if (isOpen) {
      closeDropdown()
    } else {
      setIsOpen(true);

      // setTimeout here is a workaround to make sure that the dropdownRef is actually available in the DOM by the time this executes (it pushes execution to the end of the event queue, after the rerender)
      setTimeout(() => {
        const firstItem = dropdownRef.current.querySelector('a');
        if (firstItem) firstItem.focus();
      }, 0);
    }
  }

  function closeDropdown() {
    if (!isOpen) { return }

    setIsOpen(false);
    // Return focus to the button when dropdown closes
    buttonRef.current.focus();
  }

  function handleClickOutside(event) {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      closeDropdown();
    }
  }

  function handleKeyDown(event) {
    if (event.key === ESCAPE) {
      closeDropdown();
    }
  }

  function renderDropdown() {
    return (
      <div className="dropdown" id={dropdownId} ref={dropdownRef} role="menu">
        {dropdownItems.map((item) => (
          <a className="focus-on-light" href={item.url} key={item.label} onClick={closeDropdown} role="menuitem">
            {item.label}
            {item.new ? <span className="gold-new-tag">NEW</span> : null}
          </a>
        ))}
      </div>
    );
  }

  return (
    <li className="dropdown-tab" ref={containerRef}>
      <button
        aria-controls={dropdownId}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={`focus-on-dark ${activeClass} ${openClass}`}
        onClick={toggleDropdown}
        ref={buttonRef}
        type="button"
      >
        <span>{tabName}</span>
      </button>
      {isOpen ? renderDropdown() : null}
    </li>
  );
};

const ALL_REPORTS = 'All Reports'
const CORE_REPORTS = 'Core Reports'
const DIAGNOSTIC_REPORTS = 'Diagnostic Reports'
const PREMIUM_REPORTS = 'Premium Reports'
const CURRICULUM_SPECIFIC_REPORTS = 'Curriculum-Specific Reports'

const ProgressReportsSubnavLinks = ({ showSocialStudiesTab, showScienceTab, }) => {
  const path = window.location
  const [activeTab, setActiveTab] = React.useState<string>('');

  React.useEffect(() => {
    if (path.href) {
      determineActiveTab()
    }
  }, [path.href])

  function determineActiveTab() {
    const { href } = path;

    const coreReportPaths = ['/scorebook', '/activity_packs', 'diagnostic_reports#/u/']
    const diagnosticPaths = ['/diagnostic_reports/#/diagnostics', '/diagnostic_insights']
    const premiumReportPaths = ['/activities_scores_by_classroom', '/concepts', '/standards', '/progress_reports/activity_sessions']
    const curriculumSpecificReportPaths = ['/social-studies', '/interdisciplinary-science']

    if (coreReportPaths.find(path => href.includes(path))) {
      setActiveTab(CORE_REPORTS)
    } else if (diagnosticPaths.find(path => href.includes(path))) {
      setActiveTab(DIAGNOSTIC_REPORTS)
    } else if (premiumReportPaths.find(path => href.includes(path))) {
      setActiveTab(PREMIUM_REPORTS)
    } else if (curriculumSpecificReportPaths.find(path => href.includes(path))) {
      setActiveTab(CURRICULUM_SPECIFIC_REPORTS)
    } else if (href.includes('/landing_page')) {
      setActiveTab(ALL_REPORTS)
    }
  }

  function determineClassName(tabName) {
    if (tabName === activeTab) {
      return 'focus-on-dark active'
    }

    return 'focus-on-dark'
  }

  return (
    <ul>
      <li>
        <a className={determineClassName(ALL_REPORTS)} href="/teachers/progress_reports/landing_page">{ALL_REPORTS}</a>
      </li>
      <DropdownTab
        activeTab={activeTab}
        dropdownId="core-reports-dropdown"
        dropdownItems={CORE_REPORTS_LINKS}
        tabName={CORE_REPORTS}
      />
      <DropdownTab
        activeTab={activeTab}
        dropdownId="diagnostic-reports-dropdown"
        dropdownItems={DIAGNOSTIC_REPORTS_LINKS}
        tabName={DIAGNOSTIC_REPORTS}
      />
      <DropdownTab
        activeTab={activeTab}
        dropdownId="premium-reports-dropdown"
        dropdownItems={PREMIUM_REPORTS_LINKS}
        tabName={PREMIUM_REPORTS}
      />
      {(showSocialStudiesTab || showScienceTab) && <DropdownTab
        activeTab={activeTab}
        dropdownId="curriculum-specific-reports-dropdown"
        dropdownItems={CURRICULUM_SPECIFIC_REPORTS_LINKS(showSocialStudiesTab, showScienceTab)}
        tabName={CURRICULUM_SPECIFIC_REPORTS}
      />}
    </ul>
  )
}

export default ProgressReportsSubnavLinks
